import api from './api';

/* ------------------------------------------------------------------ */
/*  Small helpers                                                     */
/* ------------------------------------------------------------------ */
// Handle new ApiEnvelope response structure from backend
const unwrap = (r) => {
  // Backend now returns ApiEnvelope<T> structure: { success: boolean, data: T, message?: string }
  if (r?.data?.success !== undefined) {
    return r.data.data;
  }
  // Fallback for legacy responses
  return r?.data?.data ?? r?.data ?? r ?? {};
};

const get = (url, cfg = {}) => api.get(url, cfg).then(unwrap);
const post = (url, body = {}, cfg = {}) => api.post(url, body, cfg).then(unwrap);
const put = (url, body = {}, cfg = {}) => api.put(url, body, cfg).then(unwrap);
const del = (url) => api.delete(url).then(unwrap);

// Utility functions for livestream access
export const isFreeLivestream = (livestream) => {
  return livestream.freeLivestream || !livestream.requiresPayment || 
    (livestream.viewersPrice !== null && livestream.viewersPrice === 0);
};

export const isPaidLivestream = (livestream) => {
  return livestream.requiresPayment && 
    (livestream.viewersPrice !== null ? livestream.viewersPrice > 0 : true);
};

export const getLivestreamAccessType = (livestream) => {
  if (isFreeLivestream(livestream)) return 'FREE';
  if (isPaidLivestream(livestream)) return 'PAID';
  return 'UNKNOWN';
};

// Helper functions for JoinLivestreamResponse
export const isJoinResponseGranted = (response) => {
  return response?.status === 'GRANTED';
};

export const isJoinResponsePaymentRequired = (response) => {
  return response?.status === 'PAYMENT_REQUIRED';
};

export const isJoinResponseStarted = (response) => {
  return response?.status === 'STARTED';
};

export const getJoinResponseLivestream = (response) => {
  return response?.livestream;
};

export const getJoinResponseSessionUrl = (response) => {
  return response?.sessionUrl;
};

export const getJoinResponseSessionId = (response) => {
  return response?.sessionId;
};

export const getJoinResponseTag = (response) => {
  return response?.tag;
};

// Enhanced helper to handle the backend response structure properly
export const parseJoinLivestreamResponse = (response) => {
  console.log('🔍 Parsing JoinLivestreamResponse:', response);
  
  if (!response) {
    return { status: 'ERROR', message: 'No response received' };
  }
  
  // Handle ApiEnvelope wrapper
  const data = response?.data || response;
  
  return {
    status: data?.status,
    livestream: data?.livestream,
    sessionId: data?.sessionId,
    sessionUrl: data?.sessionUrl,
    tag: data?.tag,
    isGranted: data?.status === 'GRANTED',
    isPaymentRequired: data?.status === 'PAYMENT_REQUIRED',
    isStarted: data?.status === 'STARTED'
  };
};

// Utility functions for new LivestreamDto fields
export const getLivestreamTeamInfo = (livestream) => {
  return {
    teamAName: livestream.teamAName,
    teamBName: livestream.teamBName,
    teamAScore: livestream.teamAScore,
    teamBScore: livestream.teamBScore,
    teamAId: livestream.teamAId,
    teamBId: livestream.teamBId,
    doubles: livestream.doubles,
    gameType: livestream.gameType
  };
};

export const getLivestreamPricing = (livestream) => {
  return {
    price: livestream.price,
    hourlyPrice: livestream.hourlyPrice,
    usageFee: livestream.usageFee,
    viewersPrice: livestream.viewersPrice,
    requiresPayment: livestream.requiresPayment,
    freeLivestream: livestream.freeLivestream,
    paymentUrl: livestream.paymentUrl,
    ownerPaid: livestream.ownerPaid
  };
};

export const getLivestreamMedia = (livestream) => {
  return {
    watchUrl: livestream.watchUrl,
    playbackUrl: livestream.playbackUrl,
    publicWatchUrl: livestream.publicWatchUrl,
    recordingUrl: livestream.recordingUrl,
    recordingEnabled: livestream.recordingEnabled
  };
};

export const getLivestreamCamera = (livestream) => {
  return {
    cameraName: livestream.cameraName,
    cameraId: livestream.cameraId
  };
};

export const getLivestreamMetadata = (livestream) => {
  return {
    id: livestream.id,
    version: livestream.version,
    description: livestream.description,
    startTime: livestream.startTime,
    endTime: livestream.endTime,
    status: livestream.status,
    courtId: livestream.courtId,
    streamKey: livestream.streamKey,
    logoType: livestream.logoType,
    sponsors: livestream.sponsors,
    liveStreamPurchaseIds: livestream.liveStreamPurchaseIds,
    updatedBy: livestream.updatedBy,
    viewerIds: livestream.viewerIds
  };
};

// Development logging helper
const withLogging = (fn, name) => {
  if (process.env.NODE_ENV !== 'development') {
    return fn;
  }

  return async (...args) => {
    console.log(`[LivestreamAPI] ${name} called with args:`, args);
    try {
      const result = await fn(...args);
      console.log(`[LivestreamAPI] ${name} successful - Full livestream object(s):`, result);
      
      // Additional logging for array responses (multiple livestreams)
      if (Array.isArray(result)) {
        console.log(`[LivestreamAPI] ${name} - Found ${result.length} livestream objects:`, result.map((ls, index) => ({
          index,
          id: ls.id,
          description: ls.description,
          status: ls.status,
          startTime: ls.startTime,
          endTime: ls.endTime,
          price: ls.price,
          requiresPayment: ls.requiresPayment
        })));
      } else if (result && typeof result === 'object') {
        // Single livestream object logging
        console.log(`[LivestreamAPI] ${name} - Livestream details:`, {
          id: result.id,
          description: result.description,
          status: result.status,
          startTime: result.startTime,
          endTime: result.endTime,
          price: result.price,
          requiresPayment: result.requiresPayment,
          courtId: result.courtId,
          sponsors: result.sponsors
        });
      }
      
      return result;
    } catch (error) {
      console.error(`[LivestreamAPI] ${name} failed:`, error);
      throw error;
    }
  };
};

/* ------------------------------------------------------------------ */
/*  Read                                                              */
/* ------------------------------------------------------------------ */
// Paginated feed endpoint with role-aware filtering
export const getLivestreamsFeed = withLogging((params = {}, signal) => {
  const queryParams = new URLSearchParams();
  
  // Add pagination parameters
  if (params.page !== undefined) queryParams.append('page', params.page);
  if (params.size !== undefined) queryParams.append('size', params.size);
  if (params.sort && params.sort.trim() !== '') queryParams.append('sort', params.sort);
  
  // Add filter parameters
  if (params.status) queryParams.append('status', params.status);
  if (params.q) queryParams.append('q', params.q);
  if (params.after) queryParams.append('after', params.after);
  if (params.before) queryParams.append('before', params.before);
  
  const url = `/livestream${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
  return get(url, { signal });
}, 'getLivestreamsFeed');

// Owner/Admin "mine" endpoint with pagination
export const getMyLivestreams = withLogging((params = {}, signal) => {
  const queryParams = new URLSearchParams();
  
  // Add pagination parameters
  if (params.page !== undefined) queryParams.append('page', params.page);
  if (params.size !== undefined) queryParams.append('size', params.size);
  if (params.sort && params.sort.trim() !== '') queryParams.append('sort', params.sort);
  
  // Add filter parameters
  if (params.status) queryParams.append('status', params.status);
  if (params.q) queryParams.append('q', params.q);
  if (params.after) queryParams.append('after', params.after);
  if (params.before) queryParams.append('before', params.before);
  
  const url = `/livestream/mine${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
  return get(url, { signal });
}, 'getMyLivestreams');

// Active streams endpoint (Admin/Owner only)
export const getActiveLivestreams = withLogging((params = {}, signal) => {
  const queryParams = new URLSearchParams();
  
  // Add pagination parameters
  if (params.page !== undefined) queryParams.append('page', params.page);
  if (params.size !== undefined) queryParams.append('size', params.size);
  if (params.sort && params.sort.trim() !== '') queryParams.append('sort', params.sort);
  
  const url = `/livestream/active${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
  return get(url, { signal });
}, 'getActiveLivestreams');

// Legacy endpoints for backward compatibility
export const getAllLivestreams = withLogging(() => getLivestreamsFeed(), 'getAllLivestreams');

export const getLivestreamById = withLogging((id, signal) => 
  get(`/livestream/${id}`, { signal }), 'getLivestreamById');
export const getLivestreamByIdPublic = withLogging((id, signal) => 
  get(`/livestream/public/${id}`, { signal }), 'getLivestreamByIdPublic');

/* ------------------------------------------------------------------ */
/*  Write / lifecycle                                                 */
/* ------------------------------------------------------------------ */
export const createLivestream = withLogging((dto) => 
  post('/livestream', dto), 'createLivestream');
export const updateLivestream = withLogging((id, dto) => 
  put(`/livestream/${id}`, dto), 'updateLivestream');
export const deleteLivestream = withLogging((id) => 
  del(`/livestream/${id}`), 'deleteLivestream');

export const startLivestream = withLogging((id) => 
  post(`/livestream/${id}/start`), 'startLivestream');
export const stopLivestream = withLogging((id) => 
  post(`/livestream/${id}/stop`), 'stopLivestream');

export const joinLivestream = withLogging((id, purchaseDto = null) => {
  console.log('🔍 API - joinLivestream called:', { id, purchaseDto });
  return post(`/livestream/${id}/join`, purchaseDto);
}, 'joinLivestream');

/* ------------------------------------------------------------------ */
/*  Payments                                                          */
/* ------------------------------------------------------------------ */
export const createCheckoutSession = withLogging((id, req) => {
  console.log('🔍 API - createCheckoutSession called:', { id, req });
  return post(`/livestream/${id}/checkout`, req);
}, 'createCheckoutSession');

export const confirmCheckout = withLogging((sessionId) => 
  get(`/livestream/checkout/confirm`, { params: { sessionId } }), 'confirmCheckout');

// Owner payment endpoints (maps to backend OwnerCheckoutUseCase)
export const createOwnerCheckoutSession = withLogging((livestreamId, paymentRequest) => 
  post(`/livestream/${livestreamId}/owner/checkout`, paymentRequest), 'createOwnerCheckoutSession');

export const confirmOwnerCheckout = withLogging((sessionId) => 
  get(`/livestream/owner/checkout/confirm`, { params: { sessionId } }), 'confirmOwnerCheckout');

// alias kept for legacy code
export const confirmLivestreamPayment = confirmCheckout;
